/* Place your scripts here */

/**
 * Splide Carousel Configuration and Initialization
 * Initializes all Splide carousels with configuration presets
 */

document.addEventListener('DOMContentLoaded', function () {
    // Global default settings inherited by all configurations
    const globalDefaults = {
    type: 'slide',
    gap: '2rem',
    arrows: false,
    pagination: true,
    autoplay: false
};

// Define Splide configurations - override only what differs from globalDefaults
const splideConfigs = {
    default: {
        perPage: 3,
        perMove: 3,
        autoplay: true
    },
    hero: {
        type: 'fade',
        rewind: true,
        perPage: 1,
        perMove: 1,
        arrows: false,
        gap: 0,
        pagination: true,
        autoplay: true,
        interval: 17000,
        autoHeight: true,
        classes: {
            pagination: 'splide__pagination absolute flex flex-col justify-center inset-auto top-0 bottom-0 right-0 xl:right-auto xl:left-[-3.2rem] p-0',
            page: 'splide__pagination__page w-2xs h-2xs roudned-full bg-white m-0 [&.is-active]:bg-teal !transform-none stuck-fade',
        },
        breakpoints: {
            640: {
                pagination: false
            }
        }
    },
    "featuredinsights": {
        type: 'loop',
        perPage: 1,
        perMove: 1,
        arrows: true,
        autoplay: true,
        gap: 0,
        pagination: false,
    },
    insights: {
        perPage: 2,
        perMove: 2,
        arrows: true,
        gap: '2rem',
        pagination: false,
        breakpoints: {
            1023: {
                perPage: 1
            }
        }
    },
    twocol: {
        perPage: 2,
        perMove: 1
    },
    threecol: {
        type: 'loop',
        perPage: 3,
        gap: 0,
        perMove: 1,
        autoplay: true,
        pauseOnFocus: false,
        pauseOnHover: false,
        pagination: false,
        arrows: false
    },
    fourcol: {
        perPage: 4,
        perMove: 1,
        pagination: false
    },
    fivecol: {
        perPage: 5,
        perMove: 1,
    },
    tworowsfourcol: {
        perPage: 4,
        perMove: 4,
        rows: 2,
    },
    imagegallerybg: {
        type: 'fade',
        rewind: true,
        perPage: 1,
        arrows: false,
        pagination: false,
        autoplay: true,
        pauseOnHover: false,
        pauseOnFocus: false,
    },
    ticker: {
        type: 'loop',
        drag: false,
        perPage: 3,
        perMove: 1,
        gap: 0,
        trimSpace: false,
        speed: 0,
        autoplay: false,
        pauseOnFocus: false,
        pauseOnHover: false,
        arrows: false,
        pagination: false,
        autoScroll: {
            speed: 0.5,
            pauseOnHover: false,
            pauseOnFocus: false,
        },
        breakpoints: {
            767: {
                perPage: 2,
                perMove: 1,
            }
        }
    }
};

// Removed: commonBreakpoints was previously applied to all configs (and merged with
// each config's own breakpoints). It was defined as:
// const commonBreakpoints = {
//     640: {
//         perPage: 1,
//         gap: '1rem'
//     },
//     768: {
//         perPage: 1,
//         gap: '1rem'
//     },
//     1024: {
//         perPage: 2,
//         gap: '1rem'
//     }
// };

// ---------------------------------------------------------------------------
// Hero slide GSAP animation
// Each slide plays a staggered entrance, holds, then fades out before the
// slider advances to the next slide automatically.
// The heading markup (.slide-hero-strong / .slide-hero-tagline) is rendered
// directly by Slide.Hero.cshtml from the TitleLine1/TitleLine2 fields, so no
// client-side normalization of the heading structure is needed here.
// ---------------------------------------------------------------------------
function initHeroAnimations(splide) {
    // Timing constants (seconds)
    // interval is the Splide config value (ms) for total time-per-slide; convert to
    // seconds and subtract the exit duration so the total cycle matches `interval`.
    const EXIT_DURATION = 0.6;  // how fast everything fades out
    const intervalSeconds = (splide.options.interval ?? 5000) / 1000;
    const HOLD_DURATION = Math.max(0, intervalSeconds - EXIT_DURATION); // how long the slide stays fully visible before exiting
    const STAGGER = 0.18; // gap between block-level elements entering

    // Cache SplitText instances by element so each element is only split once
    // and split.chars is reused on every reset/entrance cycle.
    const splitCache = new WeakMap();
    function getSplitText(el) {
        if (splitCache.has(el)) return splitCache.get(el);
        const split = new SplitText(el, { type: 'words,chars' });
        splitCache.set(el, split);
        return split;
    }

    // Per-element entrance settings: [selector, fromVars, toVars, absoluteOffset, charStagger]
    // absoluteOffset (optional): overrides the running stagger position with an exact timeline position.
    // charStagger    (optional): when true, splits text into chars and staggers them instead.
    const entranceSteps = [
        ['.slide-hero-strong', { y: 0 }, { y: 0, duration: 0.55, ease: 'power3.out' }, undefined, true],
        ['.slide-hero-tagline', { y: 0 }, { y: 0, duration: 0.5, ease: 'power2.out' }, '+=0.45', true],
        ['.slide-hero-body', { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' }],
        ['.slide-hero-cta', { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }],
    ];

    // References to the currently running timelines so we can kill them on demand
    let activeEntranceTl = null;
    let activeExitTl = null;
    let advanceTimer = null;

    function killActive() {
        if (activeEntranceTl) { activeEntranceTl.kill(); activeEntranceTl = null; }
        if (activeExitTl) { activeExitTl.kill(); activeExitTl = null; }
        if (advanceTimer) { clearTimeout(advanceTimer); advanceTimer = null; }
    }

    function resetSlide(slide) {
        entranceSteps.forEach(([sel, fromVars, , , charStagger]) => {
            const el = slide.querySelector(sel);
            if (!el) return;
            if (charStagger) {
                // Split first so char spans exist before we hide them —
                // without this the parent becomes visible while chars are absent, causing a flash.
                const split = getSplitText(el);
                // Parent must be visible so chars (which control opacity) are not double-hidden
                gsap.set(el, { autoAlpha: 1 });
                if (split.chars.length) gsap.set(split.chars, { autoAlpha: 0, y: fromVars.y ?? 0 });
            } else {
                gsap.set(el, { ...fromVars, autoAlpha: 0 });
            }
        });
    }

    function playEntrance(slide) {
        killActive();

        const tl = gsap.timeline();
        let offset = 0;

        entranceSteps.forEach(([sel, fromVars, toVars, absoluteOffset, charStagger]) => {
            const el = slide.querySelector(sel);
            if (!el) return;

            const position = absoluteOffset !== undefined ? absoluteOffset : offset;

            if (charStagger) {
                const split = getSplitText(el);
                gsap.set(el, { autoAlpha: 1 });
                gsap.set(split.chars, { autoAlpha: 0, y: fromVars.y ?? 0 });
                tl.to(split.chars, {
                    autoAlpha: 1,
                    y: toVars.y ?? 0,
                    duration: toVars.duration,
                    ease: toVars.ease,
                    stagger: 0.028,
                }, position);
            } else {
                gsap.set(el, fromVars);
                tl.to(el, toVars, position);
            }

            offset = tl.duration() + STAGGER;
        });

        activeEntranceTl = tl;

        // After the hold period, fade everything out then advance the slider
        const holdMs = (tl.duration() + HOLD_DURATION) * 1000;

        advanceTimer = setTimeout(() => {
            const exitTl = gsap.timeline({
                onComplete: () => splide.go('>')
            });

            entranceSteps.slice().reverse().forEach(([sel], i) => {
                const el = slide.querySelector(sel);
                if (!el) return;
                exitTl.to(el, { autoAlpha: 0, y: -8, duration: EXIT_DURATION, ease: 'power2.in' }, i * 0.08);
            });

            activeExitTl = exitTl;
        }, holdMs);
    }

    // Hide all slides' content up front
    splide.Components.Elements.slides.forEach(resetSlide);

    // Play entrance for subsequent slides as they become visible
    splide.on('visible', (slideObj) => {
        playEntrance(slideObj.slide);
    });

    // When a slide becomes hidden, reset it visually so it's clean for next time.
    // Do NOT call killActive() here — the visible event for the incoming slide
    // fires first and killActive() there would wipe out the new entrance timeline.
    splide.on('hidden', (slideObj) => {
        resetSlide(slideObj.slide);
    });

    // The first slide's 'visible' event fires during mount, before these
    // listeners are registered, so kick it off manually now.
    const firstSlide = splide.Components.Elements.slides[splide.index];
    if (firstSlide) playEntrance(firstSlide);
}

// Initialize all sliders
var sliders = document.querySelectorAll('.splide[data-splide-config]');
sliders.forEach(function (sliderElement) {
    var configName = sliderElement.getAttribute('data-splide-config') || 'default';

    // Merge: global defaults → specific config → breakpoints
    var config = Object.assign({}, globalDefaults, splideConfigs[configName] || splideConfigs.default);

    // Apply custom overrides if provided
    var overridesJson = sliderElement.getAttribute('data-splide-overrides');
    if (overridesJson) {
        try {
            var overrides = JSON.parse(overridesJson);
            config = Object.assign({}, config, overrides);
        } catch (e) {
            console.error('Invalid Splide config overrides JSON:', overridesJson, e);
        }
    }

    var splide = new Splide(sliderElement, config);
    if (configName === 'ticker') {
        var AutoScroll = window.splide && window.splide.Extensions && window.splide.Extensions.AutoScroll;

        splide.mount({ AutoScroll });
    } else if (configName === 'hero') {
        // Pre-hide hero content immediately so there's no flash during the 1 s delay
        sliderElement.querySelectorAll('.slide-hero-strong, .slide-hero-tagline, .slide-hero-body, .slide-hero-cta').forEach(el => {
            gsap.set(el, { autoAlpha: 0 });
        });
        // Mount immediately so Splide collapses the slides into its single-slide layout —
        // this prevents the layout shift caused by all slides stacking as block elements.
        splide.mount();
        // Hold autoplay until animations take over after the 1 s delay
        splide.Components.Autoplay.pause();
        // After 1 s, hand off to GSAP — it drives all entrances and slide advancement
        setTimeout(() => {
            initHeroAnimations(splide);
        }, 1000);
    } else {
        splide.mount();
    }

    // Keep track of the gallery background slider so we can control it via snap state
    if (sliderElement.getAttribute('data-splide-config') === 'imagegallerybg') {
        // Assign Ken Burns variation classes to each slide so the pan direction
        // cycles rather than repeating the same motion every time
        const kenBurnsVariations = ['ken-burns--pan-left', 'ken-burns--pan-right', 'ken-burns--pan-up'];
        splide.Components.Elements.slides.forEach((slide, i) => {
            slide.classList.add(kenBurnsVariations[i % kenBurnsVariations.length]);
        });

        // Reset the animation on the incoming slide so it always plays from the start
        splide.on('move', (newIndex) => {
            const incoming = splide.Components.Elements.slides[newIndex];
            if (!incoming) return;
            const img = incoming.querySelector('img');
            if (!img) return;
            img.style.animation = 'none';
            // Force reflow so the browser registers the reset before re-applying
            img.offsetWidth;
            img.style.animation = '';
        });
    }
});

// Adds "snapped" to .home-team-section-inner while it is stuck at its sticky
// position (top: -48px). getBoundingClientRect().top equals exactly STICKY_TOP_PX
// when stuck, is greater (less negative) while still scrolling toward the threshold,
// and is much less (more negative) once the parent has scrolled away.
const homeTeamSection = document.querySelector('.home-team-section-inner');
if (homeTeamSection) {
    const STICKY_TOP_PX = -48; // must match CSS top: -48px
    const tolerance = 2;

    const isStuck = () => Math.abs(homeTeamSection.getBoundingClientRect().top - STICKY_TOP_PX) <= tolerance;

    const setSnapState = (snapped) => {
        homeTeamSection.classList.toggle('snapped', snapped);
    };

    document.addEventListener('scroll', () => {
        setSnapState(isStuck());
    }, { passive: true });

    // Ensure correct state once snap momentum fully settles
    document.addEventListener('scrollend', () => {
        setSnapState(isStuck());
    });

    setSnapState(isStuck());
}
});

